/**
 * Initialization queue to prevent overwhelming the API with simultaneous calls
 * during component initialization
 */

class InitQueue {
  constructor() {
    this.queue = [];
    this.running = false;
    this.delay = 200; // 200ms between API calls
  }

  async add(fn, priority = 0) {
    return new Promise((resolve, reject) => {
      this.queue.push({
        fn,
        priority,
        resolve,
        reject
      });
      
      // Sort by priority (higher number = higher priority)
      this.queue.sort((a, b) => b.priority - a.priority);
      
      this.process();
    });
  }

  async process() {
    if (this.running || this.queue.length === 0) {
      return;
    }

    this.running = true;

    while (this.queue.length > 0) {
      const task = this.queue.shift();
      
      try {
        const result = await task.fn();
        task.resolve(result);
      } catch (error) {
        task.reject(error);
      }

      // Add delay between API calls to prevent rate limiting
      if (this.queue.length > 0) {
        await new Promise(resolve => setTimeout(resolve, this.delay));
      }
    }

    this.running = false;
  }

  // Clear the queue (useful for cleanup)
  clear() {
    this.queue.forEach(task => {
      task.reject(new Error('Queue cleared'));
    });
    this.queue = [];
    this.running = false;
  }

  // Get queue status
  getStatus() {
    return {
      queueLength: this.queue.length,
      running: this.running
    };
  }
}

// Export singleton instance
export const initQueue = new InitQueue();

// Helper function to queue API calls during initialization
export function queueApiCall(apiCallFn, priority = 0) {
  return initQueue.add(apiCallFn, priority);
}

// Priority constants for common operations
export const PRIORITY = {
  CRITICAL: 100,  // Auth, player profile
  HIGH: 50,       // Active missions/travels
  NORMAL: 10,     // Mission lists, travel cities
  LOW: 1          // Stats, economy data
};